import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useTimer } from 'hooks/withTimer';
import { useEffect } from 'react';
import { getClickStreamEvent, getEventAnalytic } from 'services/clickstream';
import { SESSION_PROLONG_TIME } from 'store/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PayHubDomainSessionsEnumsPlatformType } from 'store/models';
import {
  isSessionExpiredSelector,
  platformSelector,
  sessionExpirationTimeSelector,
  setSessionExpired,
} from 'store/slices/global';
import { redirectSelector } from 'store/slices/redirect';
import { onReloadButtonClickHandler } from 'utils';
import { useGetSessionId } from 'utils/session';

import { paySessionStateSelector } from '../../store/slices/paySessionState';
import { SessionExpiredModal } from './SessionExpired';

const clickstream = getEventAnalytic();

export const SessionExpiredModalWrapper = () => {
  const dispatch = useAppDispatch();
  const sessionId = useGetSessionId();
  const isOpen = useAppSelector(isSessionExpiredSelector);
  const sessionExpirationTime = useAppSelector(sessionExpirationTimeSelector);
  const platform = useAppSelector(platformSelector) as PayHubDomainSessionsEnumsPlatformType;
  const paySessionState = useAppSelector(paySessionStateSelector);
  const redirectData = useAppSelector(redirectSelector);
  const { generalData = {} } = paySessionState || {};

  const merchantSuccessUrl = generalData.success_url || redirectData.successUrl;
  const merchantFailUrl = generalData.fail_url || redirectData.failUrl;
  const merchantProcessingUrl = generalData.processing_url || redirectData.processingUrl;
  const merchantUrl = generalData.merchant_url || redirectData.merchantUrl;

  const { skeletonT } = useSkeletonTranslation();
  const title = skeletonT('PH.POPUP.SESSION_EXPIRED.TITLE');
  const content = skeletonT('PH.POPUP.SESSION_EXPIRED.BODY');
  const label = skeletonT('PH.BUTTON.REFRESH');
  const { startTimeout, finishTimeout, isTimeoutFinished } = useTimer({
    defaultStep: SESSION_PROLONG_TIME.L,
  });

  useEffect(() => {
    if (isOpen && sessionId && clickstream.isInitialized()) {
      const event = getClickStreamEvent.pw_session_expired_modal_open(sessionId);
      clickstream.push(event);
    }
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (sessionExpirationTime) {
      finishTimeout();
      if (sessionExpirationTime <= new Date().getTime()) {
        dispatch(setSessionExpired());
      } else {
        startTimeout(sessionExpirationTime - new Date().getTime());
      }
    }
    return () => finishTimeout();
  }, [sessionExpirationTime]);

  useEffect(() => {
    if (isTimeoutFinished) {
      dispatch(setSessionExpired());
      finishTimeout();
    }
  }, [isTimeoutFinished]);

  const onExpirationClick = () => {
    const event = getClickStreamEvent.pw_session_expired_refresh_click(sessionId);
    clickstream.push(event);

    onReloadButtonClickHandler({
      platform,
      merchantSuccessUrl,
      merchantFailUrl,
      merchantProcessingUrl,
      merchantUrl,
    });
  };

  return (
    <SessionExpiredModal isOpen={isOpen} title={title} content={content} label={label} onClick={onExpirationClick} />
  );
};
